import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, Package } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { apiErrorMessage } from '@/api/client'
import { plan as api } from '@/api/endpoints'
import { Alert, Field, Modal, Select, Spinner, Textarea } from '@/components/ui'
import { formatDate, formatDateTime, formatMoney } from '@/lib/utils'

import type {
  PlanChangeResult,
  PlanOption,
  PlanOverview,
  PlanPayment,
  PlanRequestRow,
} from './types'

/**
 * Balíček školy a prechod na iný (`contracts.upgrade`).
 *
 * Čo tlačidlo urobí, rozhoduje server podľa toho, kde škola je:
 * nezaplatená objednávka sa prepočíta, platiaca škola dostane vyšší balíček
 * hneď a faktúru na doplatok, a čo systém sám nevybaví (nižší balíček,
 * Premium, škola bez zmluvy), odíde ako požiadavka prevádzkovateľovi.
 */
export function PlanSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['plan'], queryFn: api.get })
  const [chosen, setChosen] = useState<PlanOption | null>(null)
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(
    null,
  )

  const change = useMutation({
    mutationFn: ({ key, note }: { key: string; note?: string }) => api.change(key, note),
    onSuccess: (result) => {
      setChosen(null)
      setMessage({ kind: 'success', text: resultText(result) })
      // Balíček odomyká funkcie (bootstrap) a doplatok je nová faktúra.
      void qc.invalidateQueries({ queryKey: ['plan'] })
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
      void qc.invalidateQueries({ queryKey: ['documents'] })
      void qc.invalidateQueries({ queryKey: ['billing-status'] })
    },
    onError: (error) => {
      setChosen(null)
      setMessage({ kind: 'error', text: apiErrorMessage(error) })
    },
  })

  const cancel = useMutation({
    mutationFn: api.cancelRequest,
    onSuccess: (overview) => {
      qc.setQueryData(['plan'], overview)
      setMessage({ kind: 'success', text: t('billing.plan.requestCancelled') })
    },
    onError: (error) => setMessage({ kind: 'error', text: apiErrorMessage(error) }),
  })

  function resultText(result: PlanChangeResult) {
    if (result.mode === 'request') return t('billing.plan.requestSent')
    if (result.mode === 'order') return t('billing.plan.orderChanged')
    return result.invoice
      ? t('billing.plan.upgraded', {
          invoice: result.invoice.number,
          total: formatMoney(result.invoice.total),
          due: formatDate(result.invoice.due),
        })
      : t('billing.plan.upgradedFree')
  }

  // Návrat zo Stripe. Zapisuje sa podľa webhooku, nie podľa tejto adresy -
  // preto len správa a obnovenie údajov o chvíľu.
  const cardReturn = new URLSearchParams(window.location.search).get('card')
  useEffect(() => {
    if (cardReturn !== 'ok') return
    const timer = window.setTimeout(() => {
      void qc.invalidateQueries({ queryKey: ['plan'] })
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    }, 4000)
    return () => window.clearTimeout(timer)
  }, [cardReturn, qc])

  // Odkaz zo zamknutej funkcie (`PlanLock`) vedie sem - po načítaní
  // k sekcii doskrolovať, inak by bola pod VOP mimo obrazovky.
  const { hash } = useLocation()
  useEffect(() => {
    if (data && hash === '#plan') document.getElementById('plan')?.scrollIntoView()
  }, [data, hash])

  if (!data) return null

  return (
    <section id="plan" className="space-y-3">
      <header>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Package size={15} className="text-slate-400" />
          {t('billing.plan.title')}
        </h2>
        <p className="text-xs text-slate-500">
          {t('billing.plan.current', { plan: data.plan_name || t('billing.plan.none') })}
          {' · '}
          {t(`billing.plan.hint.${data.mode}`)}
        </p>
      </header>

      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}
      {cardReturn === 'ok' ? <Alert variant="success">{t('billing.card.thanks')}</Alert> : null}
      {cardReturn === 'cancel' ? <Alert variant="info">{t('billing.card.cancelled')}</Alert> : null}

      {data.payment ? <PendingPayment payment={data.payment} /> : null}

      {data.request ? (
        <RequestStatus
          request={data.request}
          busy={cancel.isPending}
          onCancel={() => cancel.mutate()}
        />
      ) : null}

      {data.options.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {data.options.map((option) => (
            <div key={option.key} className="flex flex-col gap-2 rounded-md border border-slate-200 p-3">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{option.name}</p>
                  {option.discount_percent ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                      −{option.discount_percent} %
                    </span>
                  ) : null}
                </div>
                {option.discounted_price ? (
                  <>
                    <p className="text-sm">
                      <s className="mr-1 relative inline-block font-semibold text-yellow-600 no-underline after:pointer-events-none after:absolute after:inset-x-0 after:top-[calc(50%-3px)] after:h-[1.5px] after:bg-slate-400 after:content-['']">{formatMoney(option.price ?? '0')}</s>
                      <b>{t('billing.plan.yearly', { price: formatMoney(option.discounted_price) })}</b>
                    </p>
                    <p className="text-xs text-amber-800">
                      {t('billing.plan.discountUntil', {
                        percent: option.discount_percent,
                        date: formatDate(option.discount_until ?? null),
                      })}
                    </p>
                  </>
                ) : (
                  <p className="text-sm">
                    {t('billing.plan.yearly', { price: formatMoney(option.price ?? '0') })}
                  </p>
                )}
                {option.instructors ? (
                  <p className="text-xs text-slate-500">
                    {t('billing.plan.instructors', { count: option.instructors })}
                  </p>
                ) : null}
                {data.mode === 'upgrade' && option.amount !== null && option.amount !== undefined ? (
                  <p className="mt-1 text-xs text-slate-600">
                    {t('billing.plan.payNow', { amount: formatMoney(option.amount) })}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="btn-primary mt-auto"
                disabled={change.isPending}
                onClick={() => setChosen(option)}
              >
                {data.mode === 'order'
                  ? t('billing.plan.orderButton', { plan: option.name })
                  : t('billing.plan.upgradeButton', { plan: option.name })}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {data.options.length === 0 && data.others.length === 0 && !data.payment ? (
        <p className="text-sm text-slate-600">{t('billing.plan.topPlan')}</p>
      ) : null}

      {data.others.length > 0 ? (
        <RequestForm
          others={data.others}
          generic={data.options.length === 0}
          startOpen={data.options.length === 0 && !data.request}
          busy={change.isPending}
          onSend={(key, note) => change.mutate({ key, note })}
        />
      ) : null}

      <Modal
        open={chosen !== null}
        onClose={() => setChosen(null)}
        title={t('billing.plan.confirmTitle', { plan: chosen?.name ?? '' })}
        size="sm"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setChosen(null)}>
              {t('global.cancel')}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={change.isPending}
              onClick={() => chosen && change.mutate({ key: chosen.key })}
            >
              {change.isPending ? <Spinner /> : null}
              {t('billing.plan.confirm')}
            </button>
          </>
        }
      >
        {chosen ? <ConfirmText overview={data} option={chosen} /> : null}
      </Modal>
    </section>
  )
}

function ConfirmText({ overview, option }: { overview: PlanOverview; option: PlanOption }) {
  const { t } = useTranslation()
  const amount = formatMoney(option.amount ?? '0')

  const discount = option.discount_percent ? (
    <p className="text-amber-800">
      {t('billing.plan.discountUntil', {
        percent: option.discount_percent,
        date: formatDate(option.discount_until ?? null),
      })}
    </p>
  ) : null

  if (overview.mode === 'order') {
    return (
      <div className="space-y-2 text-sm">
        <p>{t('billing.plan.confirmOrder', { plan: option.name, amount })}</p>
        {discount}
        <p className="text-xs text-slate-500">{t('billing.plan.vatNote')}</p>
      </div>
    )
  }
  return (
    <div className="space-y-2 text-sm">
      <p>{t('billing.plan.confirmUpgrade', { plan: option.name })}</p>
      {Number(option.amount) > 0 ? (
        <p>
          {t('billing.plan.confirmInvoice', {
            amount,
            from: formatDate(overview.remaining_from),
            to: formatDate(overview.remaining_to),
          })}
        </p>
      ) : null}
      {discount}
      <p>{t('billing.plan.confirmNextYear', { plan: option.name, price: formatMoney(option.price ?? '0') })}</p>
      <p className="text-xs text-slate-500">{t('billing.plan.vatNote')}</p>
    </div>
  )
}

function PendingPayment({ payment }: { payment: PlanPayment }) {
  const { t } = useTranslation()
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function payByCard() {
    setPaying(true)
    setError(null)
    try {
      const { url } = await api.pay()
      window.location.assign(url)
    } catch (err) {
      setError(apiErrorMessage(err))
      setPaying(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 sm:flex-row">
      <div className="flex shrink-0 flex-col items-center gap-1">
        <img
          src={payment.qr_url}
          alt=""
          className="h-28 w-28 rounded bg-white p-1"
          loading="lazy"
        />
        <img src="/images/pay-by-square.svg" alt="PAY by square" className="h-6" />
      </div>
      <div className="space-y-1 text-sm">
        <p className="font-semibold">
          {t('billing.plan.awaiting', { plan: payment.plan_name })}
        </p>
        <p>
          {t('billing.plan.payAmount')}: <b>{formatMoney(payment.amount)}</b>
          {' · '}
          {t('billing.plan.vs')}: <b>{payment.variable_symbol}</b>
        </p>
        <p className="break-all text-xs text-slate-600">
          {payment.beneficiary} · {payment.iban}
          {payment.swift ? ` · ${payment.swift}` : ''}
        </p>
        <p className="text-xs text-slate-500">
          {t('billing.plan.paidFrom', { date: formatDate(payment.paid_from) })}
        </p>
        {payment.card ? (
          <div className="pt-1">
            <button type="button" className="btn-primary" onClick={payByCard} disabled={paying}>
              {paying ? <Spinner /> : <CreditCard size={14} />}
              {t('billing.card.pay', { amount: formatMoney(payment.amount) })}
            </button>
            {error ? <p className="mt-1 text-xs text-red-700">{error}</p> : null}
          </div>
        ) : null}
        <p className="flex flex-wrap gap-3 text-xs">
          {payment.proforma_url ? (
            <a href={payment.proforma_url} target="_blank" rel="noreferrer" className="text-action-600 hover:underline">
              {t('billing.plan.proforma')}
            </a>
          ) : null}
          {payment.contract_url ? (
            <a href={payment.contract_url} target="_blank" rel="noreferrer" className="text-action-600 hover:underline">
              {t('billing.plan.contractDraft')}
            </a>
          ) : null}
        </p>
      </div>
    </div>
  )
}

function RequestForm({
  others,
  generic,
  startOpen,
  busy,
  onSend,
}: {
  others: PlanOption[]
  /** Iná možnosť nie je - všeobecný nadpis namiesto „nižší, Premium“. */
  generic: boolean
  startOpen: boolean
  busy: boolean
  onSend: (key: string, note: string) => void
}) {
  const { t } = useTranslation()
  const [key, setKey] = useState(others[0]?.key ?? '')
  const [note, setNote] = useState('')
  const selected = others.find((option) => option.key === key)

  return (
    <details className="rounded-md border border-slate-200 p-3" open={startOpen}>
      <summary className="cursor-pointer text-sm font-medium">
        {generic ? t('billing.plan.requestTitleOnly') : t('billing.plan.requestTitle')}
      </summary>
      <div className="mt-3 space-y-2">
        <p className="text-xs text-slate-500">{t('billing.plan.requestHint')}</p>
        <Field label={t('billing.plan.requestPlan')} className="sm:max-w-2xl">
          <Select value={key} onChange={(e) => setKey(e.target.value)}>
            {others.map((option) => (
              <option key={option.key} value={option.key}>
                {option.name}
                {option.discounted_price
                  ? ` – ${t('billing.plan.yearlyDiscounted', {
                      price: formatMoney(option.discounted_price),
                      full: formatMoney(option.price ?? '0'),
                      percent: option.discount_percent,
                      date: formatDate(option.discount_until ?? null),
                    })}`
                  : option.price
                    ? ` – ${t('billing.plan.yearly', { price: formatMoney(option.price) })}`
                    : ''}
              </option>
            ))}
          </Select>
          {selected?.discount_percent ? (
            <p className="mt-1 text-xs text-amber-800">
              {t('billing.plan.discountUntil', {
                percent: selected.discount_percent,
                date: formatDate(selected.discount_until ?? null),
              })}
            </p>
          ) : null}
        </Field>
        <Field label={t('billing.plan.requestNote')}>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <button
          type="button"
          className="btn-secondary"
          disabled={busy || !key}
          onClick={() => onSend(key, note)}
        >
          {t('billing.plan.requestSend')}
        </button>
      </div>
    </details>
  )
}

/** Odoslaná požiadavka - aby škola po obnovení stránky vedela, o čo žiadala. */
function RequestStatus({
  request,
  busy,
  onCancel,
}: {
  request: PlanRequestRow
  busy: boolean
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const open = request.state === 'open'

  return (
    <div
      className={
        open
          ? 'space-y-1 rounded-md border border-sky-200 bg-sky-50/60 p-3 text-sm'
          : 'space-y-1 rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-sm'
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-semibold">
          {open
            ? t('billing.plan.requestOpen', { plan: request.plan_name })
            : t('billing.plan.requestDone', { plan: request.plan_name })}
        </p>
        {open ? (
          <button type="button" className="btn-secondary" disabled={busy} onClick={onCancel}>
            {t('billing.plan.requestCancel')}
          </button>
        ) : null}
      </div>
      <p className="text-xs text-slate-600">
        {t('billing.plan.requestSentAt', { date: formatDateTime(request.created) })}
        {request.requested_by ? ` · ${request.requested_by}` : ''}
      </p>
      {request.note ? (
        <p className="whitespace-pre-line text-xs text-slate-600">
          {t('billing.plan.requestNoteLabel')}: {request.note}
        </p>
      ) : null}
      {request.reply ? (
        <p className="whitespace-pre-line text-sm">
          <span className="font-medium">{t('billing.plan.requestReply')}:</span> {request.reply}
        </p>
      ) : null}
      {open ? <p className="text-xs text-slate-500">{t('billing.plan.requestReplace')}</p> : null}
    </div>
  )
}
